import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { api } from '../api/client';
import TableToolbar from './TableToolbar';
import SubTabs from './SubTabs';

type Product = {
  id: number;
  sku: string;
  name: string;
  description: string | null;
  category: string | null;
  unit_of_measure: string | null;
  roast_level: string | null;
  cost_per_unit: number;
  price_per_unit: number;
  reorder_point: number;
  reorder_quantity: number;
  supplier_id: number | null;
  supplier_name?: string;
  is_green_coffee: number;
};

type FormValues = {
  sku: string;
  name: string;
  description?: string;
  category?: string;
  unit_of_measure?: string;
  roast_level?: string;
  cost_per_unit?: number;
  price_per_unit?: number;
  reorder_point?: number;
  reorder_quantity?: number;
  supplier_id?: number;
  is_green_coffee?: boolean;
};

const InventoryPage = () => {
  const queryClient = useQueryClient();
  const [viewArchived, setViewArchived] = useState(false);
  const [activeTab, setActiveTab] = useState<'catalog' | 'create'>('catalog');
  const { register, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      unit_of_measure: 'kg',
      is_green_coffee: true
    }
  });

  const { data: products } = useQuery({
    queryKey: ['products', { viewArchived }],
    queryFn: async () =>
      api.get<Product[]>(`/api/products${viewArchived ? '?archived=1' : ''}`)
  });

  const suppliersQuery = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api.get<Array<{ id: number; name: string }>>('/api/suppliers')
  });

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'PHP',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }),
    []
  );

  const createMutation = useMutation({
    mutationFn: (values: FormValues) =>
      api.post<Product>('/api/products', {
        ...values,
        cost_per_unit: Number(values.cost_per_unit ?? 0),
        price_per_unit: Number(values.price_per_unit ?? 0),
        reorder_point: Number(values.reorder_point ?? 0),
        reorder_quantity: Number(values.reorder_quantity ?? 0),
        supplier_id: values.supplier_id ? Number(values.supplier_id) : undefined
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      reset();
    }
  });

  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<keyof Product>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const data = useMemo(() => {
    const rows = (products ?? []).filter((p) => {
      if (!filter) return true;
      const f = filter.toLowerCase();
      const hay = [
        p.sku,
        p.name,
        p.category ?? '',
        p.unit_of_measure ?? '',
        p.supplier_name ?? '',
        String(p.cost_per_unit),
        String(p.price_per_unit),
        String(p.reorder_point),
        String(p.reorder_quantity)
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(f);
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    return rows.sort((a, b) => {
      const va = (a[sortKey] as unknown) ?? '';
      const vb = (b[sortKey] as unknown) ?? '';
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [products, filter, sortKey, sortDir]);

  return (
    <div>
      <h2>Inventory</h2>
      <p style={{ color: '#64748b' }}>
        Manage SKUs, costs and reorder strategies. Products are stored in Cloudflare D1 and
        exposed via Workers APIs.
      </p>

      <SubTabs
        activeTab={activeTab}
        onChange={setActiveTab}
        tabs={[
          { id: 'catalog', label: 'Product Catalog', badge: data.length },
          { id: 'create', label: 'Add Product' }
        ]}
      />

      {activeTab === 'catalog' ? (
        <section style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ margin: 0 }}>Product catalog</h3>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#475569' }}>
              <input
                type="checkbox"
                checked={viewArchived}
                onChange={(event) => setViewArchived(event.target.checked)}
              />
              Show archived
            </label>
          </div>
          <TableToolbar value={filter} onChange={setFilter} placeholder="Filter products…" />
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  {([
                    { key: 'sku', label: 'SKU' },
                    { key: 'name', label: 'Name' },
                    { key: 'category', label: 'Category' },
                    { key: 'unit_of_measure', label: 'Unit' },
                    { key: 'supplier_name', label: 'Supplier' },
                    { key: 'cost_per_unit', label: 'Cost' },
                    { key: 'price_per_unit', label: 'Price' },
                    { key: 'reorder_point', label: 'Reorder' }
                  ] as Array<{ key: keyof Product; label: string }>).map((col) => (
                    <th
                      key={String(col.key)}
                      onClick={() => {
                        if (sortKey === col.key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                        setSortKey(col.key);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {col.label}
                      {sortKey === col.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((product) => (
                  <tr key={product.id}>
                    <td>{product.sku}</td>
                    <td>
                      {product.name}
                      {!product.is_green_coffee && <span className="badge">Roasted</span>}
                    </td>
                    <td>{product.category ?? '—'}</td>
                    <td>{product.unit_of_measure ?? 'kg'}</td>
                    <td>{product.supplier_name ?? '—'}</td>
                    <td>{currencyFormatter.format(product.cost_per_unit)}</td>
                    <td>{currencyFormatter.format(product.price_per_unit)}</td>
                    <td>
                      {product.reorder_point} / {product.reorder_quantity}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {activeTab === 'create' ? (
        <div className="card" style={{ marginTop: 16 }}>
          <h3>Add Product</h3>
          <form className="form-grid" onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
            <div className="form-group">
              <label>SKU</label>
              <input placeholder="ETH-G1" {...register('sku', { required: true })} />
            </div>
            <div className="form-group">
              <label>Name</label>
              <input placeholder="Ethiopia Guji" {...register('name', { required: true })} />
            </div>
            <div className="form-group">
              <label>Category</label>
              <input placeholder="Single Origin" {...register('category')} />
            </div>
            <div className="form-group">
              <label>Unit of measure</label>
              <input placeholder="kg" {...register('unit_of_measure')} />
            </div>
            <div className="form-group">
              <label>Roast level</label>
              <input placeholder="Medium" {...register('roast_level')} />
            </div>
            <div className="form-group">
              <label>Cost per unit</label>
              <input type="number" step="0.01" {...register('cost_per_unit', { valueAsNumber: true })} />
            </div>
            <div className="form-group">
              <label>Price per unit</label>
              <input type="number" step="0.01" {...register('price_per_unit', { valueAsNumber: true })} />
            </div>
            <div className="form-group">
              <label>Reorder point</label>
              <input type="number" step="0.1" {...register('reorder_point', { valueAsNumber: true })} />
            </div>
            <div className="form-group">
              <label>Reorder quantity</label>
              <input type="number" step="0.1" {...register('reorder_quantity', { valueAsNumber: true })} />
            </div>
            <div className="form-group">
              <label>Supplier</label>
              <select {...register('supplier_id')}>
                <option value="">Select supplier</option>
                {suppliersQuery.data?.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" {...register('is_green_coffee')} />
              <label>Green coffee</label>
            </div>
            <button className="btn primary" type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Saving…' : 'Create product'}
            </button>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default InventoryPage;
